import React, { useEffect, useState } from "react";
import { Row, Col, Container, Tab, Nav, Button, Spinner } from "react-bootstrap";
import { Case } from "../../types/case";
import { fetchCases, fetchCMUsers } from "../../services/httpClient";
import PageTitle from "../ui/PageTitle";
import ToastMsg from "../ui/ToastMsg";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import CaseTab from "./CaseTab";
import { faBriefcase, faFolderOpen, faHourglassStart, faPersonCircleQuestion, faSquareXmark } from "@fortawesome/free-solid-svg-icons";
import CaseAssignmentTab from "./CaseAssignmentTab";
import useApp from "../../utils/useApp";
import useMenuToggle from "../../utils/useMenuToggle";

interface CasesState {
  limitedAccess: boolean;
}

const Cases = ({ limitedAccess }: CasesState) => {
  const { auth, showMenu }: any = useApp();
  const toggleMenu = useMenuToggle();
  const [cmUsers, setCmUsers] = useState<any[]>([]);
  const [cases, setCases] = useState<Case[]>([]);
  const [casesFiltered, setCasesFiltered] = useState<Case[]>([]);
  const [activeCases, setActiveCases] = useState<Case[]>([]);
  const [activeCasesFiltered, setActiveCasesFiltered] = useState<Case[]>([]);
  const [waitlistCases, setWaitlistCases] = useState<Case[]>([]);
  const [waitlistCasesFiltered, setWaitlistCasesFiltered] = useState<Case[]>([]);
  const [inactiveCases, setInactiveCases] = useState<Case[]>([]);
  const [inactiveCasesFiltered, setInactiveCasesFiltered] = useState<Case[]>([]);
  const [unassignedCases, setUnassignedCases] = useState<Case[]>([]);
  const [activeEventKey, setActiveEventKey] = useState<string>("all");
  const [isLoading, setIsLoading] = useState(true);
  const [showToast, setShowToast] = useState<boolean>(false);
  const [toastBody, setToastBody] = useState<string>("");
  const [toastText, setToastText] = useState<string>("");

  useEffect(() => {
    setActiveEventKey("all");
    getCases();
    getCMUsers();

    // trackNavigationEvent("switch_view", "all_cases");
  }, [auth?.profile?.userOrgID]);

  useEffect(() => {
    setCasesFiltered(cases);
    setActiveCases(cases.filter((x: any) => x.casestatus === "active"));
    setActiveCasesFiltered(cases.filter((x: any) => x.casestatus === "active"));
    setWaitlistCases(cases.filter((x: any) => x.casestatus === "waitlist"));
    setWaitlistCasesFiltered(cases.filter((x: any) => x.casestatus === "waitlist"));
    setInactiveCases(cases.filter((x: any) => x.casestatus === "inactive"));
    setInactiveCasesFiltered(cases.filter((x: any) => x.casestatus === "inactive"));
    setUnassignedCases(cases.filter((x: any) => x.primaryCM === null || x.secondaryCM === null));
  }, [cases]);

  const getCases = () => {
    setIsLoading(true);

    fetchCases()
      .then((res: any) => {
        const c = res[0].data;
        setCases(c);
        setIsLoading(false);
      })
      .catch((err: any) => {
        console.log(err);
        setIsLoading(false);
      });
  };

  const getCMUsers = () => {
    fetchCMUsers()
      .then((res: any) => {
        const users = res[0].data.map((i: any) => {
          return {
            id: i.id,
            key: i.id,
            name: i.firstname.length === 0 ? i.email : `${i.firstname} ${i.lastname ?? ""}`,
          };
        });
        setCmUsers(users);
      })
      .catch((err: any) => {
        console.log(err);
        setIsLoading(false);
      });
  };

  const onNavClick = (route: string) => {
    // if (route) {
    //   trackNavigationEvent("switch_view", route + "_cases");
    // }

    setActiveEventKey(route);
  };

  return (
    <>
      <ToastMsg showToast={showToast} setShowToast={setShowToast} toastText={toastText} toastBody={toastBody} />

      {isLoading ? (
        <Container className={showMenu ? "lg div-main-menu custom-container px-5" : "lg div-main custom-container px-5"}>
          <Button size="sm" className="spinner-button spinner-button-main">
            <Spinner animation="border" style={{ color: "#F6893D" }} />
          </Button>
        </Container>
      ) : (
        <Container className={showMenu ? "lg div-main-menu custom-container px-5" : "lg div-main custom-container px-5"}>
          <Tab.Container defaultActiveKey={activeEventKey}>
            <Row>
              <Col sm={3}>
                <Nav
                  variant="pills"
                  className={showMenu ? "flex-column sticky-top mycase-menu-open-sticky" : "flex-column sticky-top mycase-menu-sticky"}
                >
                  <Nav.Item className="mb-2">
                    <PageTitle title={toggleMenu ? "Cases (" + cases.length + ")" : "Cases"} />
                  </Nav.Item>
                  <Nav.Item className="d-grid">
                    <Nav.Link
                      eventKey="all"
                      className={activeEventKey === "all" ? "nav-vertical-active" : "nav-vertical"}
                      onClick={() => onNavClick("all")}
                    >
                      <>
                        <FontAwesomeIcon icon={faFolderOpen} className={"me-2"} />
                        {toggleMenu ? "All (" + cases.length + ")" : "All"}
                      </>
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item className="d-grid">
                    <Nav.Link
                      eventKey="active"
                      className={activeEventKey === "active" ? "nav-vertical-active" : "nav-vertical"}
                      onClick={() => onNavClick("active")}
                    >
                      <>
                        <FontAwesomeIcon icon={faBriefcase} className={"me-2"} />
                        {toggleMenu ? "Active (" + activeCases.length + ")" : "Active"}
                      </>
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item className="d-grid">
                    <Nav.Link
                      eventKey="waitlist"
                      className={activeEventKey === "waitlist" ? "nav-vertical-active" : "nav-vertical"}
                      onClick={() => onNavClick("waitlist")}
                    >
                      <>
                        <FontAwesomeIcon icon={faHourglassStart} className={"me-2"} />
                        {toggleMenu ? "Waitlist (" + waitlistCases.length + ")" : "Waitlist"}
                      </>
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item className="d-grid">
                    <Nav.Link
                      eventKey="inactive"
                      className={activeEventKey === "inactive" ? "nav-vertical-active" : "nav-vertical"}
                      onClick={() => onNavClick("inactive")}
                    >
                      <>
                        <FontAwesomeIcon icon={faSquareXmark} className={"me-2"} />
                        {toggleMenu ? "Inactive (" + inactiveCases.length + ")" : "Inactive"}
                      </>
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <hr />
                  </Nav.Item>
                  <Nav.Item className="d-grid">
                    <Nav.Link
                      eventKey="unassigned"
                      className={activeEventKey === "unassigned" ? "nav-vertical-active" : "nav-vertical"}
                      onClick={() => onNavClick("unassigned")}
                    >
                      <>
                        <FontAwesomeIcon icon={faPersonCircleQuestion} className={"me-2"} />
                        {toggleMenu ? "Unassigned Active" : "Unassigned"}
                      </>
                    </Nav.Link>
                  </Nav.Item>
                </Nav>
              </Col>
              <Col sm={9} className="mt-2">
                <Tab.Content>
                  <Tab.Pane eventKey="all">
                    <CaseTab
                      cases={cases}
                      setCases={setCases}
                      casesFiltered={casesFiltered}
                      setCasesFiltered={setCasesFiltered}
                      cmUsers={cmUsers}
                      type="All"
                      getCases={getCases}
                      setShowToast={setShowToast}
                      setToastText={setToastText}
                      setToastBody={setToastBody}
                      limitedAccess={limitedAccess}
                    />
                  </Tab.Pane>
                  <Tab.Pane eventKey="active">
                    <CaseTab
                      cases={activeCases}
                      setCases={setCases}
                      casesFiltered={activeCasesFiltered}
                      setCasesFiltered={setActiveCasesFiltered}
                      cmUsers={cmUsers}
                      type="Active"
                      getCases={getCases}
                      setShowToast={setShowToast}
                      setToastText={setToastText}
                      setToastBody={setToastBody}
                      limitedAccess={limitedAccess}
                    />
                  </Tab.Pane>
                  <Tab.Pane eventKey="waitlist">
                    <CaseTab
                      cases={waitlistCases}
                      setCases={setCases}
                      casesFiltered={waitlistCasesFiltered}
                      setCasesFiltered={setWaitlistCasesFiltered}
                      cmUsers={cmUsers}
                      type="Waitlist"
                      getCases={getCases}
                      setShowToast={setShowToast}
                      setToastText={setToastText}
                      setToastBody={setToastBody}
                      limitedAccess={limitedAccess}
                    />
                  </Tab.Pane>
                  <Tab.Pane eventKey="inactive">
                    <CaseTab
                      cases={inactiveCases}
                      setCases={setCases}
                      casesFiltered={inactiveCasesFiltered}
                      setCasesFiltered={setInactiveCasesFiltered}
                      cmUsers={cmUsers}
                      type="Inactive"
                      getCases={getCases}
                      setShowToast={setShowToast}
                      setToastText={setToastText}
                      setToastBody={setToastBody}
                      limitedAccess={limitedAccess}
                    />
                  </Tab.Pane>
                  <Tab.Pane eventKey="unassigned">
                    <CaseAssignmentTab
                      cases={unassignedCases}
                      setCases={setCases}
                      setShowToast={setShowToast}
                      setToastText={setToastText}
                      setToastBody={setToastBody}
                    />
                  </Tab.Pane>
                </Tab.Content>
              </Col>
            </Row>
          </Tab.Container>
        </Container>
      )}
    </>
  );
};

export default Cases;
